// ==========================================================================
// Shared route-gating logic for the two auth modalities (PhoneAuthGate for
// the PWA, TelegramAuthGate for the Mini-App). Kept in a plain module — not a
// component file — so it can be unit-tested and reused without tripping the
// react-refresh only-export-components rule.
// ==========================================================================

const PUBLIC_ROUTES = ['/auth/phone', '/auth/verify-otp'];

export function isPublicRoute(pathname: string): boolean {
  if (PUBLIC_ROUTES.includes(pathname)) return true;
  // Magic link routes: /auth/:token (but not /auth/phone or /auth/verify-otp)
  if (pathname.startsWith('/auth/') && !PUBLIC_ROUTES.includes(pathname)) return true;
  return false;
}

export function isOnboardingRoute(pathname: string): boolean {
  return (
    pathname === '/welcome' ||
    pathname.startsWith('/onboarding') ||
    pathname === '/location-setup'
  );
}

/**
 * /payout/phone is a deep-link entry point, not an onboarding destination.
 * The bot's "Share phone number" web_app button links straight here, and a
 * shared URL can too. That intent is unambiguous — the user was explicitly
 * asked for their number — so the phone-capture form must render regardless
 * of onboarding/verification state instead of being hijacked into onboarding.
 */
export function isPhoneCaptureRoute(pathname: string): boolean {
  return pathname === '/payout/phone';
}

/**
 * PWA (PhoneAuthGate) flag-driven flow. Assumes an authenticated user — the
 * token/public-route check lives in the gate component.
 */
export function getRequiredRoute(
  user: { onboarded: boolean; verified: boolean },
  pathname: string
): string | null {
  // Honor the phone-capture deep-link before any onboarding gate.
  if (isPhoneCaptureRoute(pathname)) return null;

  const isPublic = isPublicRoute(pathname);
  const isOnboarding = isOnboardingRoute(pathname);

  if (!user.onboarded) {
    if (pathname === '/welcome' || pathname.startsWith('/onboarding') || isPublic) {
      return null;
    }
    return '/welcome';
  }

  if (!user.verified) {
    if (pathname !== '/location-setup') {
      return '/location-setup';
    }
    return null;
  }

  if (isOnboarding) {
    return '/home';
  }

  if (isPublic) {
    if (!user.onboarded) return '/welcome';
    if (!user.verified) return '/location-setup';
    return '/home';
  }

  return null;
}

/**
 * Mini-App (TelegramAuthGate) flow mirrors the PWA one, minus the
 * public-routes branch — there is no /auth/phone path inside the Mini-App.
 */
export function getTelegramRequiredRoute(
  user: { onboarded: boolean; verified: boolean },
  pathname: string
): string | null {
  // Honor the bot's phone-capture deep-link before any onboarding gate.
  if (isPhoneCaptureRoute(pathname)) return null;

  const isOnboarding = isOnboardingRoute(pathname);

  if (!user.onboarded) {
    if (pathname === '/welcome' || pathname.startsWith('/onboarding')) return null;
    return '/welcome';
  }
  if (!user.verified) {
    if (pathname !== '/location-setup') return '/location-setup';
    return null;
  }
  if (isOnboarding) return '/home';
  return null;
}
